<template>
  <div class="flex flex-col min-h-screen">
    <!-- Main content area -->
    <div class="flex-grow py-8" v-auto-animate>
      <component
        class="flex flex-col gap-y-5 items-center justify-center"
        :is="currentStepComponent"
        :isLoading="isLoading"
        @update="updateFormData"
        @nextStep="goToNextStep"
        @previousStep="goToPreviousStep"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DateValue } from "@ark-ui/vue/date-picker";
import * as Sentry from "@sentry/nuxt";
import { computed, ref } from "vue";
import type { NewEventFormProps } from "~/assets/types/newEventForm";
import DatesSelectionStep from "~/components/newEventForm/DatesSelectionStep.vue";
import TitleAndDescriptionStep from "~/components/newEventForm/TitleAndDescriptionStep.vue";
import type { CreateEventSchema } from "~/server/api/events/index.post";

const localePath = useLocalePath();
const i18n = useI18n();
const dayjs = useDayjs();

// Define steps and their corresponding components
const steps = [
  { component: TitleAndDescriptionStep, name: "Title and Description" },
  { component: DatesSelectionStep, name: "Date Selection" },
];

const currentStepIndex = ref(0);
const isLoading = ref(false);

const formData = ref<NewEventFormProps>({
  title: "",
  description: "",
  dates: [],
});

// Computed property to get the current step's component
const currentStepComponent = computed(
  () => steps[currentStepIndex.value].component
);

const updateFormData = (updatedData: {
  title?: string;
  description?: string;
  dates?: DateValue[];
}) => {
  formData.value = {
    ...formData.value,
    ...Object.fromEntries(
      Object.entries(updatedData).filter(
        ([_, v]) => v !== undefined && v !== null
      )
    ),
  };
};

// Function to go to the next step
const goToNextStep = () => {
  if (currentStepIndex.value >= steps.length - 1) {
    createForm();
  }
  if (currentStepIndex.value < steps.length - 1) {
    currentStepIndex.value++;
  }
};

async function createForm() {
  isLoading.value = true;
  const dates = formData.value.dates.map((d) => dayjs(d).toISOString());

  const { data, error } = await useFetch("/api/events", {
    method: "post",
    body: <CreateEventSchema>{
      title: formData.value.title,
      description: formData.value.description,
      dates: dates,
    },
  });

  if (error.value) {
    console.log(error.value);
    Sentry.captureException(error.value);
    useToast().add({
      title: i18n.t("global.errorMessages.toasts.unknownError.title"),
      description: i18n.t(
        "global.errorMessages.toasts.unknownError.description"
      ),
    });
  }

  if (data.value !== null) {
    useToast().add({ title: i18n.t("newEventForm.toasts.eventCreated") });
    const t = await navigateTo(localePath(`/events/${data.value}`));
  }
  isLoading.value = false;

  return;
}

// Function to go to the previous step
const goToPreviousStep = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
};
</script>

<style scoped>
/* Style the dots for navigation */
.dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  display: inline-block;
}
</style>
